import React, { Component } from 'react';
import PropTypes from 'prop-types';
import PropTypesImmutable from 'react-immutable-proptypes';
import { Form, Field } from 'react-final-form';
import { connect } from 'react-redux';
import { Row, Col, Button } from 'antd';

import { TextField, RadioField, DateField, NumberField } from '../../atoms';
import { RegisterActions, AutoCompleteCompanies } from '../../components';

import {
  FORM_GENERAL,
  generalFormFields,
} from '../../constants/formsAddEquipment';

import {
  updateProductionUnit,
  addProductionUnit,
  activateProductionUnit,
  deactivateProductionUnit,
  fetchProductionUnitByEicw,
} from '../../actions/actionCreators/productionUinitActionCreators';

import { selectOwner as selectorOwnerAction } from '../../actions/actionCreators/productionUnitAddActionCreators';
import { productionUnitFields } from '../../constants/productionUnit';
import { fuelType, productionUnitStatus } from '../../constants/classificators';
import { setDuplicateEicwError } from '../../reducers/eicwValidatorReducer';

import FormStateToRedux from '../FormStateToRedux';
import SubmitErrorToast from '../SubmitErrorToast';

import './AddEquipmentGeneral.css';
import {
  formValueSelector,
  updateFormState,
} from '../../reducers/reactFinalFormReducer';
import { isEqual } from 'lodash/fp';
import { makeRffValidateFromLegacy } from '../rffLegacyValidateAdapter';
import validateSchema from './validateSchema';

export class AddEquipmentGeneral extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    // nav/buttons
    goNext: PropTypes.func,
    hasPrevious: PropTypes.bool,
    hasDone: PropTypes.bool,
    isInvestmentSupport: PropTypes.bool,
    goPrevious: PropTypes.func,
    // context and permissions
    inEditForNotAdmin: PropTypes.bool.isRequired,
    isEditMode: PropTypes.bool.isRequired,
    isAdmin: PropTypes.bool.isRequired,
    selectProductionUnitOwner: PropTypes.bool.isRequired,
    isHydrogenContext: PropTypes.bool,
    // data
    productionUnit: PropTypesImmutable.map.isRequired,
    initialValues: PropTypes.object.isRequired,
    legalEntityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    duplicateEicwError: PropTypes.bool,
    // actions
    selectOwner: PropTypes.func.isRequired,
    activate: PropTypes.func.isRequired,
    deactivate: PropTypes.func.isRequired,
    fetchProductionUnitByEicw: PropTypes.func.isRequired,
    updateProductionUnit: PropTypes.func.isRequired,
    addProductionUnit: PropTypes.func.isRequired,
    setDuplicateEicwError: PropTypes.func.isRequired,
    // mirror to redux store
    updateFormState: PropTypes.func.isRequired,
    showRequestChangeModal: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.validate = makeRffValidateFromLegacy(
      validateSchema,
      props,
      props.initialValues
    );
  }

  componentDidMount() {
    const values = this.props.initialValues;
    this.props.updateFormState(FORM_GENERAL, { values });
  }

  onSubmit = async (values) => {
    const {
      fetchProductionUnitByEicw,
      updateProductionUnit,
      addProductionUnit,
      setDuplicateEicwError,
      legalEntityId,
      goNext,
    } = this.props;

    const currentId = values[generalFormFields.ID];
    const eicw = values[generalFormFields.EICW_CODE];

    const found = await fetchProductionUnitByEicw(eicw);
    if (found?.result && currentId !== found.result) {
      setDuplicateEicwError(true);
      return;
    }
    setDuplicateEicwError(false);

    if (currentId) {
      await updateProductionUnit(values);
    } else {
      await addProductionUnit({
        ...values,
        [generalFormFields.LEGAL_ENTITY_ID]: legalEntityId,
      });
    }

    if (goNext) goNext();
  };

  render() {
    const {
      t,
      hasPrevious,
      hasDone,
      goPrevious,
      isInvestmentSupport,
      inEditForNotAdmin,
      isEditMode,
      isAdmin,
      selectProductionUnitOwner,
      isHydrogenContext,
      legalEntityId,
      selectOwner,
      activate,
      deactivate,
      duplicateEicwError,
      initialValues,
      productionUnit,
      showRequestChangeModal,
    } = this.props;

    const unitStatus = productionUnit.get(productionUnitFields.STATUS);
    const unitId = productionUnit.get(productionUnitFields.ID);
    const eicwInitial = productionUnit.get(productionUnitFields.EICW_CODE);

    const renderFirstTimeUseField = ({
      t,
      isHydrogenContext,
      inEditForNotAdmin,
      legalEntityId,
    }) => (
      <Field
        className="date-field"
        name={generalFormFields.FIRST_TIME_USE}
        component={DateField}
        required
        label={t('general.firstTimeUse')}
        tooltip={t(
          isHydrogenContext
            ? 'general.firstTimeUseTooltipHydrogen'
            : 'general.firstTimeUseTooltip'
        )}
        disabled={inEditForNotAdmin || !legalEntityId}
      />
    );

    return (
      <div>
        {selectProductionUnitOwner && (
          <AutoCompleteCompanies
            onChange={selectOwner}
            value={legalEntityId}
            isHydrogenManufacturerSearch={isHydrogenContext}
          />
        )}

        <Form
          key={unitId || 'new'}
          initialValues={initialValues}
          initialValuesEqual={isEqual}
          validate={this.validate}
          onSubmit={this.onSubmit}
          subscription={{
            submitting: true,
            pristine: true,
            submitFailed: true,
            submitError: true,
            submitCount: true,
          }}
          render={({
            handleSubmit,
            submitFailed,
            submitError,
            submitCount,
          }) => {
            return (
              <form onSubmit={handleSubmit}>
                <FormStateToRedux form={FORM_GENERAL} />
                <SubmitErrorToast
                  submitFailed={submitFailed}
                  submitError={submitError}
                  submitCount={submitCount}
                />

                <h2>
                  {isEditMode ? t('general.titleEdit') : t('general.title')}
                </h2>

                <Row gutter={32}>
                  <Col span={12}>
                    <Field
                      label={t('general.name')}
                      tooltip={t('general.nameTooltip')}
                      required
                      name={generalFormFields.NAME}
                      component={TextField}
                      disabled={inEditForNotAdmin || !legalEntityId}
                    />
                  </Col>

                  {(isEditMode || isAdmin) && (
                    <Col span={isHydrogenContext ? 12 : 6}>
                      {/* Single Field with render-prop to compute className from its own value */}
                      <Field name={generalFormFields.EICW_CODE}>
                        {({ input, meta }) => {
                          const isInvalidDuplicate =
                            input.value !== undefined &&
                            duplicateEicwError === true;
                          return (
                            <>
                              <TextField
                                input={input}
                                meta={meta} // TextField can render meta.error/meta.submitError
                                required={isAdmin}
                                type="text"
                                label={t('general.eicwCode')}
                                disabled={inEditForNotAdmin || !legalEntityId}
                                className={
                                  isInvalidDuplicate ? 'eicw-error-field' : ' '
                                }
                              />
                              {isInvalidDuplicate && (
                                <p style={{ color: '#f5222d' }}>
                                  {t('general.eicwCodeError')}
                                </p>
                              )}
                            </>
                          );
                        }}
                      </Field>
                    </Col>
                  )}
                  {!isHydrogenContext && (
                    <Col span={6}>
                      <Field
                        name={generalFormFields.EECS_CODE}
                        component={TextField}
                        type="text"
                        label={t('general.eecsCode')}
                        disablePlaceholder={true}
                        disabled={true}
                      />
                    </Col>
                  )}
                </Row>

                <Row gutter={32}>
                  <Col span={12}>
                    <Field
                      name={generalFormFields.ADDRESS}
                      component={TextField}
                      required
                      type="text"
                      label={t('general.address')}
                      disabled={inEditForNotAdmin || !legalEntityId}
                    />
                  </Col>
                  {!isHydrogenContext ? (
                    <Col span={12}>
                      <Field
                        name={generalFormFields.LOCALITY}
                        component={TextField}
                        required
                        type="text"
                        label={t('general.locality')}
                        disabled={inEditForNotAdmin || !legalEntityId}
                      />
                    </Col>
                  ) : (
                    <Col span={12}>
                      {renderFirstTimeUseField({
                        t,
                        isHydrogenContext,
                        inEditForNotAdmin,
                        legalEntityId,
                      })}
                    </Col>
                  )}
                </Row>
                {!isHydrogenContext && (
                  <Row gutter={32}>
                    <Col span={12}>
                      <Field
                        name={generalFormFields.ZIP_CODE}
                        component={TextField}
                        required
                        type="text"
                        label={t('general.zipCode')}
                        disabled={inEditForNotAdmin || !legalEntityId}
                      />
                    </Col>
                    <Col span={12}>
                      {renderFirstTimeUseField({
                        t,
                        isHydrogenContext,
                        inEditForNotAdmin,
                        legalEntityId,
                      })}
                    </Col>
                  </Row>
                )}
                <Row gutter={32}>
                  <Col span={12}>
                    <Field
                      name={generalFormFields.IS_INVESTMENT_SUPPORT}
                      component={RadioField}
                      required
                      options={[
                        {
                          value: true,
                          label: t('general.isInvestmentSupport.trueLabel'),
                        },
                        {
                          value: false,
                          label: t('general.isInvestmentSupport.falseLabel'),
                        },
                      ]}
                      type="text"
                      label={t('general.isInvestmentSupport.label')}
                      disabled={inEditForNotAdmin || !legalEntityId}
                    />
                  </Col>
                  <Col span={12}>
                    {isInvestmentSupport && (
                      <Field
                        name={generalFormFields.AMOUNT_OF_INVESTMENT}
                        component={NumberField}
                        required
                        label={t('general.amountOfInvestment')}
                        disabled={inEditForNotAdmin || !legalEntityId}
                        step={1}
                        min={0}
                        isFloat
                        parse={(v) =>
                          v === '' || v == null ? undefined : Number(v)
                        }
                      />
                    )}
                  </Col>
                </Row>

                {/* Subscribe to the EICW value to control the Activate/Deactivate button */}
                <Field
                  name={generalFormFields.EICW_CODE}
                  subscription={{ value: true }}
                >
                  {({ input }) => {
                    const isActivateBtnDisabled =
                      eicwInitial !== input.value || !eicwInitial;
                    return (
                      <RegisterActions
                        showRequestChangeModal={showRequestChangeModal}
                        hasPrevious={hasPrevious}
                        hasDone={hasDone}
                        isEditMode={isEditMode}
                        isAdmin={isAdmin}
                        goPrevious={goPrevious}
                        showSaveButton={isAdmin || !isEditMode}
                        inEditForNotAdmin={inEditForNotAdmin}
                        t={t}
                      >
                        {unitId && isEditMode && isAdmin && (
                          <Button
                            disabled={isActivateBtnDisabled}
                            size="large"
                            type="primary"
                            onClick={
                              unitStatus === productionUnitStatus.ACTIVE
                                ? () => deactivate(unitId)
                                : () => activate(unitId)
                            }
                          >
                            {unitStatus === productionUnitStatus.ACTIVE
                              ? t('general.deactivateBtn')
                              : t('general.activateBtn')}
                          </Button>
                        )}
                      </RegisterActions>
                    );
                  }}
                </Field>
              </form>
            );
          }}
        />
      </div>
    );
  }
}

const selector = formValueSelector(FORM_GENERAL);

const mapStateToProps = (state, ownProps) => {
  let pu = ownProps.productionUnit;
  if (pu.get('productionDeviceType') === undefined) {
    pu = pu.set(
      'productionDeviceType',
      ownProps.isHydrogenContext ? fuelType.HYDROGEN : fuelType.BIOMETHANE
    );
  }

  return {
    productionUnit: pu,
    initialValues: pu.toJS(),
    duplicateEicwError: state.toJS().eicwValidatorReducer.duplicateEicwError,
    isInvestmentSupport: selector(
      state,
      generalFormFields.IS_INVESTMENT_SUPPORT
    ),
  };
};

const mapDispatchToProps = {
  selectOwner: selectorOwnerAction,
  activate: activateProductionUnit,
  deactivate: deactivateProductionUnit,
  fetchProductionUnitByEicw,
  updateProductionUnit,
  addProductionUnit,
  setDuplicateEicwError,
  updateFormState,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AddEquipmentGeneral);
